import React, { useState, useCallback, useRef, useEffect } from "react";
import { Editor, EditorState, RichUtils, ContentState } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import "draft-js/dist/Draft.css";
import {
  Box,
  IconButton,
  Stack,
  Paper,
  Tooltip,
  Divider,
  Typography,
  Fade
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  Code
} from "@mui/icons-material";

const CustomTextArea = ({
  placeholder = "Write something...",
  onChange,
  value,
  minHeight = 120,
  maxHeight = 300,
  showToolbar = true,
  outputFormat = "html", // 'html', 'plain', or 'both'
  error = false,
  helperText = "",
  disabled = false,
  // New label props
  label = "",
  required = false,
  fullWidth = true,
  size = "medium", // 'small' | 'medium'
  // Optional character limit
  maxChars
}) => {
  const editorRef = useRef(null);
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [focused, setFocused] = useState(false);
  const isInitialized = useRef(false);

  // Initialize editor with value when component mounts or value changes
  useEffect(() => {
    if (value && !isInitialized.current) {
      try {
        const contentState = createContentStateFromValue(value, outputFormat);
        const newEditorState = EditorState.createWithContent(contentState);

        // Move selection to the end of the content
        const selectionState =
          EditorState.moveSelectionToEnd(newEditorState).getSelection();
        const updatedEditorState = EditorState.forceSelection(
          newEditorState,
          selectionState
        );

        setEditorState(updatedEditorState);
        isInitialized.current = true;
      } catch (error) {
        console.error("Error initializing editor with value:", error);
        setEditorState(EditorState.createEmpty());
      }
    }
  }, [value, outputFormat]);

  // Handle editor state change and notify parent
  const handleChange = useCallback(
    (newEditorState) => {
      if (disabled) return;
      
      // If maxChars is set, enforce the limit by preventing additional input beyond the limit
      if (typeof maxChars === 'number' && maxChars > 0) {
        const plainText = newEditorState.getCurrentContent().getPlainText();
        const overBy = plainText.length - maxChars;
        if (overBy > 0) {
          // Truncate content to maxChars
          const truncated = plainText.slice(0, maxChars);
          const truncatedContent = ContentState.createFromText(truncated);
          const truncatedState = EditorState.push(
            newEditorState,
            truncatedContent,
            'insert-characters'
          );
          setEditorState(truncatedState);
          newEditorState = truncatedState;
        } else {
          setEditorState(newEditorState);
        }
      } else {
        setEditorState(newEditorState);
      }

      if (onChange) {
        const contentState = newEditorState.getCurrentContent();

        let output;

        if (outputFormat === "html") {
          output = stateToHTML(contentState);
        } else if (outputFormat === "plain") {
          output = contentState.getPlainText();
        } else if (outputFormat === "both") {
          output = {
            html: stateToHTML(contentState),
            plain: contentState.getPlainText()
          };
        }

        onChange(output);
      }
    },
    [onChange, outputFormat, disabled]
  );

  // Handle inline formatting
  const handleInlineStyle = (style) => {
    if (disabled) return;
    handleChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  // Handle block formatting
  const handleBlockType = (blockType) => {
    if (disabled) return;
    handleChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  // Focus the editor when container is clicked
  const focusEditor = () => {
    if (disabled) return;
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  // Get character count
  const getCharacterCount = () => {
    return editorState.getCurrentContent().getPlainText().length;
  };

  // Check if a style is active
  const isStyleActive = (style) => {
    const currentStyle = editorState.getCurrentInlineStyle();
    return currentStyle.has(style);
  };

  // Check if a block type is active
  const isBlockActive = (blockType) => {
    const selection = editorState.getSelection();
    const block = editorState
      .getCurrentContent()
      .getBlockForKey(selection.getStartKey());
    return block.getType() === blockType;
  };

  // Handle key commands for formatting
  const handleKeyCommand = (command) => {
    if (disabled) return "not-handled";
    
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  // Handle editor focus
  const handleFocus = () => {
    if (disabled) return;
    setFocused(true);
  };

  // Handle editor blur
  const handleBlur = () => {
    setFocused(false);
  };

  // Calculate padding based on size
  const getPadding = () => {
    return size === "small" ? 1 : 2;
  };

  // Calculate minHeight offset for different sizes
  const getMinHeightOffset = () => {
    return size === "small" ? 60 : 70;
  };

  const hasContent = getCharacterCount() > 0;

  return (
    <Box 
      sx={{ 
        position: "relative",
        mb: 2,
        width: fullWidth ? "100%" : "auto"
      }}
    >
      {/* Label - positioned like MUI TextField with proper sizing */}
      {label && (
        <Typography
          component="label"
          variant={focused || hasContent ? "body2" : "body1"}
          sx={{
            position: "absolute",
            top: 0,
            left: 14,
            transform: focused || hasContent 
              ? "translateY(-50%) scale(0.75)" 
              : `translateY(${size === 'small' ? '10px' : '14px'})`,
            transformOrigin: "top left",
            transition: "all 0.2s ease-out",
            color: error 
              ? "error.main" 
              : disabled
              ? "text.disabled"
              : focused 
              ? "primary.main" 
              : "text.secondary",
            backgroundColor: focused || hasContent 
              ? "background.paper" 
              : "transparent",
            px: 1,
            zIndex: 1,
            pointerEvents: "none",
            fontSize: focused || hasContent ? "1.05rem" : "1rem",
            fontWeight: 400,
            lineHeight: 1,
            maxWidth: "calc(100% - 32px)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {label}
          {required && (
            <Typography 
              component="span" 
              sx={{ 
                color: error ? "error.main" : "error.main",
                ml: 0.5
              }}
            >
              *
            </Typography>
          )}
        </Typography>
      )}

      <Paper
        elevation={focused && !disabled ? 1 : 0}
        sx={{
          p: getPadding(),
          minHeight: minHeight,
          border: "1px solid",
          borderColor: error
            ? "error.main"
            : disabled
            ? "action.disabled"
            : focused
            ? "primary.main"
            : "divider",
          borderRadius: 1,
          transition: "all 0.2s ease",
          backgroundColor:  "background.paper",
          "&:hover": !disabled ? {
            borderColor: error
              ? "error.main"
              : focused
              ? "primary.main"
              : "grey.400"
          } : {},
          cursor: disabled ? "not-allowed" : "text",
          opacity: 1, // Remove opacity change for disabled state
          position: "relative"
        }}
        onClick={focusEditor}
      >
        {/* Compact Toolbar */}
        {showToolbar && (
          <Fade in={(focused || hasContent) && !disabled}>
            <Stack
              direction="row"
              spacing={0.5}
              mb={1}
              sx={{
                flexWrap: "wrap",
                gap: 0.5,
                opacity: focused && !disabled ? 1 : 0.5,
                transition: "opacity 0.2s ease",
                ...(disabled && {
                  opacity: 0.5
                })
              }}
            >
              <Tooltip title={disabled ? "Disabled" : "Bold"}>
                <span>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleInlineStyle("BOLD")}
                    color={isStyleActive("BOLD") ? "primary" : "default"}
                    disabled={disabled}
                    sx={{ 
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        color: "action.disabled",
                        opacity: 0.5
                      }
                    }}
                  >
                    <FormatBold fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={disabled ? "Disabled" : "Italic"}>
                <span>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleInlineStyle("ITALIC")}
                    color={isStyleActive("ITALIC") ? "primary" : "default"}
                    disabled={disabled}
                    sx={{ 
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        color: "action.disabled",
                        opacity: 0.5
                      }
                    }}
                  >
                    <FormatItalic fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={disabled ? "Disabled" : "Underline"}>
                <span>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleInlineStyle("UNDERLINE")}
                    color={isStyleActive("UNDERLINE") ? "primary" : "default"}
                    disabled={disabled}
                    sx={{ 
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        color: "action.disabled",
                        opacity: 0.5
                      }
                    }}
                  >
                    <FormatUnderlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ 
                mx: 0.5,
                ...(disabled && {
                  borderColor: "action.disabled"
                })
              }} />

              <Tooltip title={disabled ? "Disabled" : "Bulleted List"}>
                <span>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleBlockType("unordered-list-item")}
                    color={
                      isBlockActive("unordered-list-item") ? "primary" : "default"
                    }
                    disabled={disabled}
                    sx={{ 
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        color: "action.disabled",
                        opacity: 0.5
                      }
                    }}
                  >
                    <FormatListBulleted fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={disabled ? "Disabled" : "Numbered List"}>
                <span>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleBlockType("ordered-list-item")}
                    color={
                      isBlockActive("ordered-list-item") ? "primary" : "default"
                    }
                    disabled={disabled}
                    sx={{ 
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        color: "action.disabled",
                        opacity: 0.5
                      }
                    }}
                  >
                    <FormatListNumbered fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={disabled ? "Disabled" : "Blockquote"}>
                <span>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleBlockType("blockquote")}
                    color={isBlockActive("blockquote") ? "primary" : "default"}
                    disabled={disabled}
                    sx={{ 
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        color: "action.disabled",
                        opacity: 0.5
                      }
                    }}
                  >
                    <FormatQuote fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={disabled ? "Disabled" : "Code Block"}>
                <span>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleBlockType("code-block")}
                    color={isBlockActive("code-block") ? "primary" : "default"}
                    disabled={disabled}
                    sx={{ 
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        color: "action.disabled",
                        opacity: 0.5
                      }
                    }}
                  >
                    <Code fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Fade>
        )}

        {/* Editor */}
        <Box
          sx={{
            borderRadius: 0.5,
            minHeight: minHeight - getMinHeightOffset(),
            maxHeight: maxHeight,
            p: 0.5,
            cursor: disabled ? "not-allowed" : "text",
            overflow: "auto",
            opacity: 1, // Remove opacity change
            "& .public-DraftEditor-content": {
              minHeight: minHeight - getMinHeightOffset() - 20,
              cursor: disabled ? "not-allowed" : "text",
              fontSize: size === "small" ? "0.875rem" : "1rem",
              lineHeight: 1.5,
              paddingTop: label ? (focused || hasContent ? 0.5 : 0) : 0,
              color: disabled ? "text.disabled" : "text.primary",
              // Match MUI's disabled text color
              ...(disabled && {
                color: "text.disabled"
              })
            },
            "& .public-DraftEditorPlaceholder-root": {
              fontSize: size === "small" ? "0.875rem" : "1rem",
              paddingTop: label ? (focused || hasContent ? 0.5 : 0) : 0,
              color: disabled ? "text.disabled" : "text.secondary",
              // Match MUI's disabled placeholder color
              ...(disabled && {
                color: "text.disabled"
              })
            },
            "& .public-DraftEditor-content, & .public-DraftEditorPlaceholder-root": {
              ...(disabled && {
                cursor: "not-allowed"
              })
            }
          }}
        >
          <Editor
            ref={editorRef}
            editorState={editorState}
            onChange={handleChange}
            handleKeyCommand={handleKeyCommand}
            placeholder={focused || !label ? placeholder : ""}
            onFocus={handleFocus}
            onBlur={handleBlur}
            readOnly={disabled}
          />
        </Box>
      </Paper>

      {/* Helper text and character count */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5, ml: 1 }}>
        {/* Helper text on left */}
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? "error.main" : disabled ? "text.disabled" : "text.secondary"
            }}
          >
            {helperText}
          </Typography>
        )}
        
        {/* Character count on right */}
        {(focused || hasContent) && !disabled && (
          <Fade in>
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {typeof maxChars === 'number' && maxChars > 0
                ? `${getCharacterCount()} / ${maxChars} characters`
                : `${getCharacterCount()} characters`}
            </Typography>
          </Fade>
        )}
      </Box>
    </Box>
  );
};

// Helper function to create content state from value
const createContentStateFromValue = (value, outputFormat) => {
  try {
    if (outputFormat === "html" && typeof value === "string") {
      // Use draft-js-import-html to properly parse HTML content
      return stateFromHTML(value);
    } else if (outputFormat === "plain" && typeof value === "string") {
      return ContentState.createFromText(value);
    } else if (
      outputFormat === "both" &&
      value &&
      typeof value.plain === "string"
    ) {
      return ContentState.createFromText(value.plain);
    }
  } catch (e) {
    console.error("Error creating content state from value:", e);
    // Fallback to empty content state
    return ContentState.createFromText("");
  }

  return ContentState.createFromText("");
};

export default CustomTextArea;